import type { DataProvider, GetListParams, GetOneParams } from 'react-admin';
import { RewareAdminTypes } from '@troverecommerce/shared-reware-api';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { VAS_STATUSES } from './constants';
import { fetchInventoryItems } from './fetchInventoryItems';
import type {
  ApiSort,
  BooleanFieldOption,
  FetchInventoryItemsVariables,
  InventoryItemRecord,
} from './types';

// String filters, sent trimmed (blank → undefined).
const PASSTHROUGH_FILTERS = [
  'state',
  'condition',
  'sku',
  'parentSku',
  'brand',
  'searchQuery',
  'uuid',
  'troveSerialNumber',
  'upc',
  'partnerSku',
  'title',
  'size',
  'color',
  'season',
  'department',
  'category',
  'rejectedReason',
  'availability',
  'availabilityCheckin',
  'custodyStatus',
  'resaleUpc',
  'preprintedLpn',
  'shopifyProductId',
  'shopifyVariantId',
  'fabric',
  'warehouseCode',
  'box',
  'position',
  'locationType',
  'wmsStatus',
] as const satisfies readonly (keyof FetchInventoryItemsVariables)[];

// Yes/No selects ('true' | 'false') sent as reware-api YES/NO.
const BOOLEAN_FILTERS = [
  'newWithTags',
  'isFlagged',
  'needsPhotography',
  'needsRepair',
  'needsCleaning',
  'isKeyless',
] as const satisfies readonly (keyof FetchInventoryItemsVariables)[];

// Free-text numeric ID filters, sent as Int.
const NUMERIC_FILTERS = [
  'itemId',
  'catalogId',
  'shipmentId',
  'locationId',
] as const satisfies readonly (keyof FetchInventoryItemsVariables)[];

type PassthroughFilter = typeof PASSTHROUGH_FILTERS[number];
type BooleanFilter = typeof BOOLEAN_FILTERS[number];
type NumericFilter = typeof NUMERIC_FILTERS[number];

// The list filter values react-admin threads through (it types `filter` as
// `any`); narrowing here keeps the mapping below type-checked.
type InventoryFilter =
  Partial<Record<PassthroughFilter, string>>
  & Partial<Record<BooleanFilter, string>>
  & Partial<Record<NumericFilter, string | number>>
  & { price?: string | number, vasStatus?: string };

/**
 * react-admin sort → reware-api sort codes; only ID and update time are
 * supported. `UPDATE_*` orders by `state_transition_at`, which is also what the
 * "Last updated" column displays — so the visible value and the ordering agree,
 * even though neither moves for a non-state edit such as a price change.
 */
const toApiSort = (sort?: { field: string, order: string }): ApiSort => {
  if (!sort) return 'ID_DESC';
  const direction = sort.order === 'ASC' ? 'ASC' : 'DESC';
  return sort.field === 'updatedAt' ? `UPDATE_${direction}` : `ID_${direction}`;
};

// Coerce a free-text numeric filter, dropping blank (incl. whitespace-only —
// Number(' ') is 0) and non-numeric input.
const toNumber = (value: string | number | undefined): number | undefined => {
  const text = typeof value === 'string' ? value.trim() : value;
  if (text == null || text === '') return undefined;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// GraphQL's Int is a 32-bit signed integer; sending a decimal or an
// out-of-range value (e.g. a UPC pasted into Item ID) fails the whole query.
// The filter inputs reject these before we get here — see isGraphQLInt in
// InventoryItemList — so this is a backstop for values arriving from a saved
// list state or a hand-edited URL.
export const GRAPHQL_INT_MIN = -2_147_483_648;
export const GRAPHQL_INT_MAX = 2_147_483_647;
const asGraphQLInt = (value: number | undefined): number | undefined =>
  value != null && Number.isInteger(value) && value >= GRAPHQL_INT_MIN && value <= GRAPHQL_INT_MAX ?
    value :
    undefined;

const toInteger = (value: string | number | undefined): number | undefined =>
  asGraphQLInt(toNumber(value));

// Price is entered in dollars; reware-api matches the integer cents column exactly.
const toCents = (value: string | number | undefined): number | undefined => {
  const dollars = toNumber(value);
  return dollars == null ? undefined : asGraphQLInt(Math.round(dollars * 100));
};

const toBooleanOption = (value: string | undefined): BooleanFieldOption | undefined => {
  if (value === 'true') return RewareAdminTypes.BooleanFieldOptions.Yes;
  if (value === 'false') return RewareAdminTypes.BooleanFieldOptions.No;
  return undefined;
};

/**
 * The combined "Job status" filter, whose value is `jobType:STATUS`.
 * The job type is matched against VAS_JOB_TYPES and the status against
 * VAS_STATUSES, so neither half of a stale saved filter or a hand-edited URL
 * reaches the API unchecked. Written as an explicit switch rather than a
 * computed key (`{ [jobType.code]: status }`), which TypeScript widens to a
 * string index signature — that would accept any variable name at all.
 */
const toVasStatus = (value: string | undefined): Partial<FetchInventoryItemsVariables> => {
  const [type, status] = value?.split(':') ?? [];
  if (!status || !VAS_STATUSES.some(({ code }) => code === status)) return {};
  switch (type) {
    case 'repairStatus': return { repairStatus: status };
    case 'cleaningStatus': return { cleaningStatus: status };
    case 'photographyStatus': return { photographyStatus: status };
    default: return {};
  }
};

const toApiVariables = (params: GetListParams): FetchInventoryItemsVariables => {
  const { page = 1, perPage = 25 } = params.pagination ?? {};
  const filter = (params.filter ?? {}) as InventoryFilter;

  const variables: FetchInventoryItemsVariables = {
    partnerId: requirePartner().id,
    page,
    pageSize: perPage,
    sort: toApiSort(params.sort),
    price: toCents(filter.price),
    ...toVasStatus(filter.vasStatus),
  };
  PASSTHROUGH_FILTERS.forEach((key) => { variables[key] = filter[key]?.trim() || undefined; });
  BOOLEAN_FILTERS.forEach((key) => { variables[key] = toBooleanOption(filter[key]); });
  NUMERIC_FILTERS.forEach((key) => { variables[key] = toInteger(filter[key]); });
  // reware-api throws when Parent SKU accompanies a matched Shopify ID (it
  // calls .some() on the string parent SKU the match sets), so drop it.
  if (variables.shopifyProductId || variables.shopifyVariantId) variables.parentSku = undefined;
  return variables;
};

/**
 * Read-only provider: only getList and getOne are supported. The remaining
 * DataProvider methods are stubbed to throw so an unsupported call fails with a
 * clear message rather than an opaque "undefined is not a function" (same stub
 * pattern as src/shopifySync/dataProvider.ts).
 */
interface InventoryItemsDataProvider {
  getList: (
    resource: string,
    params: GetListParams
  ) => Promise<{ data: InventoryItemRecord[], total: number }>;
  getOne: (
    resource: string,
    params: GetOneParams
  ) => Promise<{ data: InventoryItemRecord }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

const inventoryItemsDataProviderImpl: InventoryItemsDataProvider = {
  getList: async (_resource, params) => {
    const result = await fetchInventoryItems(toApiVariables(params));
    return { data: result.rows, total: result.count };
  },

  getOne: async (_resource, params) => {
    // Guard before querying: a non-numeric id would serialize to a null
    // `itemId`, dropping the constraint entirely, and the unfiltered query
    // would hand back the partner's newest item as if it were the match.
    const itemId = toInteger(params.id);
    if (itemId == null) {
      throw new Error(`Invalid inventory item id: ${String(params.id)}`);
    }
    const result = await fetchInventoryItems({
      partnerId: requirePartner().id,
      page: 1,
      pageSize: 1,
      sort: 'ID_DESC',
      itemId,
    });
    if (!result.rows.length) {
      throw new Error(`Inventory item ${params.id} not found`);
    }
    return { data: result.rows[0] };
  },

  getMany: async () => { throw new Error('getMany not implemented for inventoryItems resource'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for inventoryItems resource'); },
  create: async () => { throw new Error('create not implemented for inventoryItems resource'); },
  update: async () => { throw new Error('update not implemented for inventoryItems resource'); },
  updateMany: async () => { throw new Error('updateMany not implemented for inventoryItems resource'); },
  delete: async () => { throw new Error('delete not implemented for inventoryItems resource'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for inventoryItems resource'); },
};

// React-admin's DataProvider methods are generic, which prevents a type-safe
// single-resource provider; we define proper types above and cast here.
export const inventoryItemsDataProvider =
  inventoryItemsDataProviderImpl as unknown as DataProvider;
